import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "../../config/axiosInstance";
import { CRM, CLIENTS } from "../../config/apiConfig";

/*
  Dados e ações de UMA conta.

  Existe porque a ficha (`/clientes/:id`) e o painel lateral de trabalho
  (AccountDrawer) fazem exatamente as mesmas coisas — registrar contato, criar
  tarefa, concluir tarefa, fixar nota — e a primeira versão tinha esse bloco
  duplicado. Duplicado, a invalidação de cache diverge: o painel atualizava a
  fila e a ficha não, então concluir uma tarefa pela ficha deixava a conta
  fantasma na fila até o refetch de 4 minutos.
*/

const CRM_SCOPES = [
  ["crm", "queues"],
  ["crm", "tasks"],
  ["crm", "pipeline"],
];

const useCrmAccount = (companyId, { enabled = true } = {}) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState(null);

  const key = ["crm", "client", String(companyId)];
  const isEnabled = Boolean(companyId) && enabled;

  const relationship = useQuery(
    key,
    async () => {
      const { data } = await axios.get(`${CRM.CLIENT}/${companyId}`);
      return data.data;
    },
    { enabled: isEnabled, retry: false }
  );

  /*
    As métricas de uso saem da listagem de clientes filtrada por "#id" — o
    mesmo cálculo da tabela e do pipeline. Um endpoint novo só para isto
    recalcularia o que já existe, com risco de divergir nos números.
  */
  const metrics = useQuery(
    [...key, "metrics"],
    async () => {
      const { data } = await axios.get(`${CLIENTS.LIST}?search=%23${companyId}`);
      return (
        data.data?.data?.find((row) => String(row.id) === String(companyId)) ||
        null
      );
    },
    { enabled: isEnabled, retry: false }
  );

  const stages = useQuery(
    ["crm", "stages"],
    async () => {
      const { data } = await axios.get(CRM.STAGES);
      return data.data;
    },
    { staleTime: 60 * 60 * 1000, refetchInterval: false }
  );

  const stageLabel = (stage) =>
    (stages.data || []).find((item) => item.id === stage)?.label || stage;

  const invalidate = () => {
    queryClient.invalidateQueries(key);
    CRM_SCOPES.forEach((scope) => queryClient.invalidateQueries(scope));
  };

  const onError = (requestError) =>
    setError(
      requestError.response?.data?.message ||
        "Não foi possível concluir a ação."
    );

  /*
    As cinco mutations compartilham o mesmo pós-processamento: limpar o erro,
    invalidar a conta e invalidar o que depende dela (filas, tarefas,
    pipeline). O objeto é montado uma vez e passado às cinco — chamar
    `useMutation` dentro de um helper quebraria as regras de hooks.
  */
  const options = {
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError,
  };

  const addInteraction = useMutation(
    ({ type, body }) =>
      axios.post(`${CRM.INTERACTIONS}/${companyId}/interactions`, {
        type,
        body,
      }),
    options
  );

  const addTask = useMutation(
    (payload) => axios.post(`${CRM.CLIENT_TASKS}/${companyId}/tasks`, payload),
    options
  );

  const setTaskStatus = useMutation(
    ({ taskId, status }) => axios.put(`${CRM.TASK}/${taskId}`, { status }),
    options
  );

  const togglePin = useMutation(
    ({ interactionId, pinned }) =>
      axios.put(`${CRM.INTERACTION}/${interactionId}/pin`, { pinned }),
    options
  );

  const removeInteraction = useMutation(
    (interactionId) => axios.delete(`${CRM.INTERACTION}/${interactionId}`),
    options
  );

  return {
    relationship,
    metrics,
    stageLabel,
    account: relationship.data,
    usage: metrics.data,
    error,
    clearError: () => setError(null),
    addInteraction,
    addTask,
    setTaskStatus,
    togglePin,
    removeInteraction,
  };
};

export default useCrmAccount;
